import { computed, type ComputedRef } from 'vue'
import { useAppConfig } from '@/composables/useAppConfig'
import { useLocale } from '@/composables/useLocale'
import { formatCentsAsCurrency, formatPercentage } from '@/utils/currencyFormat'

// Mirror the backend defaults (config/startline.php and config/credits.php)
// so the marketing copy renders a correctly-formatted, sensible fee before
// the public app-config fetch resolves - or forever, if it fails - instead
// of flashing an empty/undefined rate. useAppConfig() swallows fetch errors
// and leaves `config` as `null`, so these defaults are also the permanent
// fallback on failure, which is safe: they match what ships in production
// unless an operator changes the env vars, and are still a real, formatted
// rate rather than a broken placeholder.
const DEFAULT_FEE_PERCENTAGE = 2.5
const DEFAULT_FEE_PERCENTAGE_BASIS_POINTS = 250
const DEFAULT_FEE_FIXED_CENTS = 50
const DEFAULT_CREDIT_PRICE_PER_CREDIT = 1
const DEFAULT_CREDIT_CURRENCY = 'eur'

interface OrganizerFeeDisplay {
  formattedFeePercentage: ComputedRef<string>
  formattedFeeFixed: ComputedRef<string>
  formattedCreditRate: ComputedRef<string>
  registrationFeeParams: ComputedRef<{ percentage: string, fixed: string }>
}

/**
 * Locale-aware, config-driven formatting of the platform's public pricing
 * (per-registration fee and email-credit price) for use in marketing copy.
 * Consumed by both the pricing cards and the FAQ answer that quotes the same
 * fee, so the two never drift from each other or from the configured value.
 */
export function useOrganizerFeeDisplay(): OrganizerFeeDisplay {
  const { config } = useAppConfig()
  const { currentLocale } = useLocale()

  // Fall back at the whole sub-object level - not per-field - to match the
  // convention used by every other useAppConfig() consumer (AppFooter.vue,
  // EventRacesPage.vue, OrganizerTermsPage.vue, ParticipantTermsPage.vue,
  // RaceRegistrationPage.vue). This also protects against a cached CDN/browser
  // response (Cache-Control: public, max-age=3600, s-maxage=86400 on
  // ShowAppConfigController) that predates the `fees`/`credits` keys existing
  // at all: `config.value` can be truthy while `fees`/`credits` are missing,
  // and reading a field off `undefined` would throw instead of falling back.
  const fees = computed(() => config.value?.fees ?? {
    percentage: DEFAULT_FEE_PERCENTAGE,
    percentage_basis_points: DEFAULT_FEE_PERCENTAGE_BASIS_POINTS,
    fixed_cents: DEFAULT_FEE_FIXED_CENTS,
  })
  const credits = computed(() => config.value?.credits ?? { price_per_credit: DEFAULT_CREDIT_PRICE_PER_CREDIT, currency: DEFAULT_CREDIT_CURRENCY })

  const feePercentage = computed(() => fees.value.percentage)
  const feeFixedCents = computed(() => fees.value.fixed_cents)
  const creditPricePerCredit = computed(() => credits.value.price_per_credit)
  const creditCurrency = computed(() => credits.value.currency)

  const formattedFeePercentage = computed(() => formatPercentage(feePercentage.value, currentLocale.value))
  // The platform-fee fixed component has no currency field in the API contract
  // by design - the `fees` block is implicitly EUR-only (see backend handoff).
  const formattedFeeFixed = computed(() => formatCentsAsCurrency(feeFixedCents.value, 'eur', currentLocale.value))
  const formattedCreditRate = computed(() =>
    formatCentsAsCurrency(creditPricePerCredit.value, creditCurrency.value, currentLocale.value),
  )

  const registrationFeeParams = computed(() => ({
    percentage: formattedFeePercentage.value,
    fixed: formattedFeeFixed.value,
  }))

  return {
    formattedFeePercentage,
    formattedFeeFixed,
    formattedCreditRate,
    registrationFeeParams,
  }
}
